<p-table
  #dt
  selectionMode="multiple"
  class="transcript-table"
  columnResizeMode="expand"
  scrollHeight="flex"
  sortMode="multiple"
  stateStorage="session"
  [stateKey]="stateKey"
  [dataKey]="'transId'"
  [virtualScroll]="true"
  [virtualScrollItemSize]="tableSize === 'small' ? 51 : 71"
  [multiSortMeta]="initialSort"
  [globalFilterFields]="[
    'fileName',
    'student_name',
    'extractionStatus',
    'formattedDate',
    'confidence_score',
    'lockedByUser'
  ]"
  [resizableColumns]="true"
  [value]="documents()"
  [rowTrackBy]="trackByTranscriptId"
  [rowSelectable]="isRowSelectable"
  [columns]="cols"
  [scrollable]="true"
  [size]="tableSize"
  [attr.aria-label]="headerLabelKey() | translate"
  [(selection)]="selectedDocuments"
  (onStateRestore)="ignoreSavedColumnWidths(dt)"
>
  <ng-template pTemplate="caption">
    <div class="table-header-row">
      <div class="flex items-center align-items-center">
        <span class="table-header-label">{{ headerLabelKey() | translate }}</span>
        @if (dt.filteredValue && dt.filteredValue.length !== documents().length) {
          <span class="text-lg filtered-count">
            {{
              'ITP.filteredCountOfTotal'
                | translate: { filtered: dt.filteredValue.length, total: documents().length }
            }}
          </span>
          <p-button
            class="dap-clear-filters"
            icon="fa-solid fa-filter-slash"
            severity="secondary"
            tooltipPosition="top"
            [pTooltip]="'ITP.clearAllFilters' | translate"
            [ariaLabel]="'ITP.clearAllFilters' | translate"
            [text]="true"
            (onClick)="clearAllFilters()"
          >
          </p-button>
        } @else {
          <span class="text-lg">({{ documents().length }})</span>
        }
      </div>
      <ng-content select="[transcriptUploadButtons]"></ng-content>
    </div>
    <div class="flex gap-2 table-header-controls">
      <div class="flex gap-1">
        <p-iconfield>
          <p-inputicon class="pi pi-search" />
          <input
            #globalSearch
            class="dap-search-queue search-queue"
            pInputText
            id="transcript-search"
            [attr.aria-label]="'ITP.searchTranscriptQueue' | translate"
            [placeholder]="'ITP.searchTranscriptQueue' | translate"
            (input)="onGlobalFilter($event)"
          />
        </p-iconfield>
      </div>
      @if (isItpPowerProcessor() && itpTypeFilterFlag()) {
        <div class="relative">
          <p-chip
            class="dap-hs-filter-chip filter-chip"
            role="button"
            [ariaLabel]="'ITP.hsTranscripts' | translate"
            [class.selected]="activeTypeFilter() === transcriptType.HighSchool"
            [tabIndex]="0"
            (click)="onFilterByType(transcriptType.HighSchool)"
            (keydown.enter)="onFilterByType(transcriptType.HighSchool)"
            (keydown.space)="onFilterByType(transcriptType.HighSchool)"
          >
            <div class="filter-chip-content">
              {{ 'ITP.hsTranscripts' | translate }}
              @if (activeTypeFilter() === transcriptType.HighSchool) {
                <i class="absolute fa-solid fa-circle-xmark remove-filter-icon"></i>
              }
            </div>
          </p-chip>
        </div>
        <div class="relative">
          <p-chip
            class="dap-college-filter-chip filter-chip"
            role="button"
            [ariaLabel]="'ITP.collegeTranscripts' | translate"
            [class.selected]="activeTypeFilter() === transcriptType.College"
            [tabIndex]="0"
            (click)="onFilterByType(transcriptType.College)"
            (keydown.enter)="onFilterByType(transcriptType.College)"
            (keydown.space)="onFilterByType(transcriptType.College)"
          >
            <div class="filter-chip-content">
              {{ 'ITP.collegeTranscripts' | translate }}
              @if (activeTypeFilter() === transcriptType.College) {
                <i class="absolute fa-solid fa-circle-xmark remove-filter-icon"></i>
              }
            </div>
          </p-chip>
        </div>
      }
      <div class="flex ml-auto action-buttons">
        @if (selectedDocuments.length > 0) {
          <p-tag
            severity="info"
            [value]="selectableDocumentsCount + ' Selected'"
            [rounded]="true"
          ></p-tag>
          <p-divider layout="vertical" />
          <p-button
            class="dap-export-btn"
            severity="secondary"
            size="small"
            [label]="'ITP.sendToSIS' | translate"
            [disabled]="!canSendToSIS"
            (onClick)="confirmSendToSisDialog()"
          >
          </p-button>
          <p-divider layout="vertical" />
          <div class="flex gap-2">
            <p-button
              severity="secondary"
              variant="outlined"
              size="small"
              class="dap-assign-btn white-bg-button"
              tooltipPosition="top"
              showDelay="500"
              [label]="'ITP.assignTo' | translate"
              [pTooltip]="!canAssign ? ('ITP.assignToTooltip' | translate) : ''"
              [disabled]="!canAssign"
              (onClick)="onAssign()"
            >
            </p-button>

            @if (selectedDocuments.length > 0 && canUnassign) {
              <p-button
                severity="secondary"
                variant="outlined"
                size="small"
                icon="fa-solid fa-unlock"
                class="dap-unassign-btn white-bg-button"
                [label]="'transcriptData.unassign' | translate"
                [disabled]="!canUnassign"
                (click)="onUnassign()"
              ></p-button>
            }
          </div>
          <p-divider layout="vertical" />
          <p-button
            class="dap-delete-transcripts-btn"
            icon="fa-solid fa-trash"
            severity="danger"
            size="small"
            [disabled]="!canDelete"
            [ariaLabel]="'ITP.deleteSelected' | translate"
            (onClick)="confirmDeleteDialog()"
          >
          </p-button>
        }
        <p-divider layout="vertical" />
        <p-button
          class="dap-large-table-btn"
          icon="fa-solid fa-server"
          severity="secondary"
          size="small"
          tooltipPosition="top"
          showDelay="500"
          [text]="activeTableSize"
          [ariaLabel]="'ITP.largeTableSize' | translate"
          [pTooltip]="'ITP.largeTableSize' | translate"
          (onClick)="setTableSize('large')"
        >
        </p-button>
        <p-button
          class="dap-small-table-btn"
          icon="fa-solid fa-grip-lines"
          severity="secondary"
          size="small"
          tooltipPosition="top"
          showDelay="500"
          [text]="!activeTableSize"
          [ariaLabel]="'ITP.smallTableSize' | translate"
          [pTooltip]="'ITP.smallTableSize' | translate"
          (onClick)="setTableSize('small')"
        >
        </p-button>
      </div>
    </div>
  </ng-template>

  <ng-template pTemplate="header">
    <tr>
      <th>
        <span class="sr-only">Select Row</span>
        <p-tableHeaderCheckbox
          class="dap-select-all"
          [ariaLabel]="'ITP.selectAll' | translate"
        ></p-tableHeaderCheckbox>
      </th>
      @for (col of visibleCols; track col.field) {
        <th
          pResizableColumn
          scope="col"
          [pSortableColumn]="col.sortable ? col.field : undefined"
          [class]="'col-' + col.field"
        >
          <div class="header-content">
            <div class="header-content-left">
              <span>{{ col.header | translate }}</span>
              @if (col.sortable) {
                <p-sortIcon [field]="col.field" />
              }
            </div>
            @switch (col.filterType) {
              @case ('text') {
                <p-columnFilter
                  display="menu"
                  type="text"
                  [field]="col.field"
                  [showOperator]="true"
                  [showAddButton]="true"
                />
              }
              @case ('date') {
                <p-columnFilter
                  display="menu"
                  type="date"
                  matchMode="createdOn"
                  [field]="col.field"
                  [matchModeOptions]="dateMatchModes"
                  [showOperator]="true"
                  [showAddButton]="true"
                />
              }
            }
          </div>
        </th>
      }
    </tr>
  </ng-template>
  <ng-template
    let-document
    pTemplate="body"
  >
    <tr
      class="document-row"
      [ngClass]="{
        'disabled-row': disableDocuments(document)
      }"
    >
      <td>
        <p-tableCheckbox
          [disabled]="isLockedByOtherUser(document) && !isITPAdmin"
          [value]="document"
          [ariaLabel]="('ITP.select' | translate) + ' ' + document.fileName"
        ></p-tableCheckbox>
      </td>
      @for (col of visibleCols; track col.field) {
        <td>
          @switch (col.field) {
            @case ('fileName') {
              <a
                class="dap-open-transcript-link document-link"
                [ngClass]="{ 'disabled-document': disableDocuments(document) }"
                [attr.tabindex]="disableDocuments(document) ? -1 : 0"
                [attr.href]="
                  disableDocuments(document)
                    ? null
                    : '/transcripts/transcript-upload?id=' + document.transId
                "
                (click)="openDocument($event, document)"
                (keyup.enter)="openDocument($event, document)"
              >
                <span
                  tooltipPosition="top"
                  showDelay="500"
                  [pTooltip]="document.fileName"
                >
                  {{ document.fileName }}
                </span>
              </a>
            }
            @case ('extractionStatus') {
              <div class="status-container">
                <p-button
                  class="dap-status-btn"
                  tooltipPosition="right"
                  showDelay="500"
                  [attr.aria-label]="
                    document.confidence_score +
                    ('confidenceModal.viewConfidenceReasoning' | translate)
                  "
                  [pTooltip]="'confidenceModal.viewConfidenceReasoning' | translate"
                  [label]="getStatusTranslation(document.extractionStatus) | translate"
                  [ngClass]="getStatusClass(document.extractionStatus)"
                  (click)="showConfidenceModal($event, document)"
                ></p-button>
              </div>
            }
            @case ('createdAt') {
              <span class="creation-date">
                {{ document.createdAt | date: 'MM/dd/yyyy hh:mm a' }}
              </span>
            }
            @case ('typeId') {
              <span>
                @if (document.typeMismatch) {
                  <i
                    class="fa-duotone fa-solid fa-triangle-exclamation warning-icon"
                    role="img"
                    tooltipPosition="top"
                    showDelay="500"
                    [attr.aria-label]="'ITP.typeMismatchTooltip' | translate"
                    [pTooltip]="'ITP.typeMismatchTooltip' | translate"
                  ></i>
                }
                {{ getTypeLabel(document.typeId) }}
              </span>
            }
            @case ('lockedByUser') {
              <div class="status-container">
                @if (document.lockedByUser) {
                  <p-chip
                    icon="fa-solid fa-user"
                    tooltipPosition="left"
                    showDelay="300"
                    [label]="document.lockedByUser"
                    [pTooltip]="document.lockedByUser"
                    [ngClass]="getStatusClass('Assigned')"
                  >
                  </p-chip>
                }
              </div>
            }
            @default {
              {{ getFieldValue(document, col.field) }}
            }
          }
        </td>
      }
    </tr>
  </ng-template>

  <ng-template pTemplate="emptymessage">
    <tr>
      <td
        class="text-center"
        [attr.colspan]="visibleCols.length + 1"
      >
        @if (isLoading()) {
          <div
            class="flex justify-content-center align-items-center p-4 gap-2 loading-transcripts"
            role="status"
            aria-live="polite"
          >
            <i
              class="fa-solid fa-spinner fa-pulse small-spinner"
              aria-hidden="true"
            ></i>
            <span class="loading-message"> {{ 'ITP.loadingTranscripts' | translate }} </span>
          </div>
        } @else if (!isLoading() && dt.filteredValue && dt.filteredValue.length === 0) {
          <div class="empty-message-container">
            <span> {{ 'ITP.noDocumentsUploaded' | translate }} </span>
          </div>
        }
      </td>
    </tr>
  </ng-template>
</p-table>

<p-dialog
  header="{{ 'transcriptData.deleteTranscript' | translate }}"
  [modal]="true"
  [dismissableMask]="true"
  [closable]="true"
  [resizable]="false"
  [draggable]="false"
  [(visible)]="deleteConfirmationVisible"
  (onHide)="deleteConfirmationVisible = false"
>
  <p
    class="dialog-content"
    [innerHTML]="
      'transcriptData.deleteTranscriptMessage' | translate: { count: selectableDocumentsCount }
    "
  ></p>
  <p
    class="confirm-message"
    [innerHTML]="
      'transcriptData.confirmDeleteTranscript' | translate: { count: selectableDocumentsCount }
    "
  ></p>
  <div class="dialog-footer">
    <p-button
      class="dap-cancel-delete"
      variant="text"
      [label]="'cancel' | translate"
      (onClick)="deleteConfirmationVisible = false"
    ></p-button>
    <p-button
      class="dap-confirm-delete"
      severity="danger"
      variant="outlined"
      [label]="'delete' | translate"
      (onClick)="onDeleteDocument()"
    ></p-button>
  </div>
</p-dialog>

<p-dialog
  header="{{ 'ITP.bulkDialogHeader' | translate }}"
  [modal]="true"
  [dismissableMask]="true"
  [closable]="true"
  [resizable]="false"
  [draggable]="false"
  [(visible)]="sendToSISConfirmationVisible"
  (onHide)="sendToSISConfirmationVisible = false"
>
  <p
    class="dialog-content"
    [innerHTML]="'ITP.bulkSendToSisMessage' | translate: { count: selectableDocumentsCount }"
  ></p>
  <p
    class="confirm-message"
    [innerHTML]="'ITP.confirmSendToSisTranscript' | translate: { count: selectableDocumentsCount }"
  ></p>
  <div class="dialog-footer">
    <p-button
      class="dap-cancel-export"
      variant="text"
      [label]="'cancel' | translate"
      (onClick)="sendToSISConfirmationVisible = false"
    ></p-button>
    <p-button
      class="dap-confirm-export"
      severity="secondary"
      variant="outlined"
      [label]="'ITP.sendToSis' | translate"
      (onClick)="onSendToSis()"
    ></p-button>
  </div>
</p-dialog>

<!-- Confidence Score Modal -->
<app-itp-extraction-confidence
  [field]="{
    value: selectedConfidenceScore,
    bbox: null,
    confidence: selectedConfidenceScore,
    page: null
  }"
  [customExplanation]="selectedConfidenceExplanation"
  [fieldsNeedingAttention]="selectedFieldsNeedingAttention"
  [visible]="confidenceModalVisible"
  (visibleChange)="onConfidenceModalClose()"
>
</app-itp-extraction-confidence>
